'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { getAllCourses, getCourseById, Course } from '@/lib/curriculumData';
import { Send, CheckCircle2, CalendarDays, Clock, Wallet, GraduationCap } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { LEVELS } from '@/lib/site';

const steps = [
  { title: '온라인 신청', desc: '수강 신청 폼 작성 및 제출' },
  { title: '레벨 테스트', desc: '무료 레벨 테스트 및 상담 (방문 또는 온라인)' },
  { title: '과정 확정', desc: '레벨 테스트 결과에 따른 적합한 과정 추천' },
  { title: '등록 및 결제', desc: '수강료 납부 및 등록 완료' },
  { title: '수업 시작', desc: '교재 배부 및 첫 수업 진행' },
];

function EnrollmentForm() {
  const searchParams = useSearchParams();
  const courseIdFromUrl = searchParams.get('courseId');

  const [courses, setCourses] = useState<Course[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    courseId: courseIdFromUrl || '',
    startDate: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setCourses(getAllCourses());
    if (courseIdFromUrl) {
      setFormData(prev => ({ ...prev, courseId: courseIdFromUrl }));
    }
  }, [courseIdFromUrl]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 실제로는 서버로 데이터를 전송해야 합니다
    console.log('수강 신청 데이터:', formData);

    // localStorage에 임시 저장
    const enrollments = JSON.parse(localStorage.getItem('enrollments') || '[]');
    enrollments.push({
      ...formData,
      id: Date.now().toString(),
      submittedAt: new Date().toISOString()
    });
    localStorage.setItem('enrollments', JSON.stringify(enrollments));

    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // 3초 후 폼 초기화
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        courseId: '',
        startDate: '',
        message: ''
      });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const selectedCourse = formData.courseId ? getCourseById(formData.courseId) : null;
  const L = selectedCourse ? LEVELS[selectedCourse.level] : null;

  return (
    <>
      <PageHeader en="Enrollment" title="수강 신청" description="원하시는 과정을 선택하고 정보를 입력해주세요." />

      <section className="bg-cream pb-20 sm:pb-28">
        <div className="wrap grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {submitted && (
              <div className="mb-5 flex items-start gap-3 rounded-3xl bg-forest p-6 text-white">
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-sun" />
                <div>
                  <p className="text-lg font-bold">신청이 완료되었습니다!</p>
                  <p className="text-sm text-white/70">빠른 시일 내에 연락드리겠습니다.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="rounded-[32px] bg-white p-7 sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block sm:col-span-2">
                  <span className="field-label">과정 선택 <span className="text-forest-600">*</span></span>
                  <select name="courseId" value={formData.courseId} onChange={handleChange} required className="field">
                    <option value="">과정을 선택해주세요</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name} - {course.price.toLocaleString()}원
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="field-label">이름 <span className="text-forest-600">*</span></span>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="홍길동" className="field" />
                </label>
                <label className="block">
                  <span className="field-label">전화번호 <span className="text-forest-600">*</span></span>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="010-1234-5678" className="field" />
                </label>
                <label className="block">
                  <span className="field-label">이메일 <span className="text-forest-600">*</span></span>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="example@email.com" className="field" />
                </label>
                <label className="block">
                  <span className="field-label">희망 시작일</span>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="field" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="field-label">문의사항</span>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="궁금하신 사항이나 요청사항을 입력해주세요"
                    className="field resize-none"
                  />
                </label>
              </div>

              <button type="submit" className="btn-coral mt-8 w-full py-4 text-base">
                <Send className="h-4 w-4" />
                수강 신청하기
              </button>
              <p className="mt-4 text-center text-sm text-muted">
                <span className="text-forest-600">*</span> 필수 입력 · 신청 후 1-2일 내에 담당자가 연락드립니다.
              </p>
            </form>
          </div>

          <aside className="space-y-5 lg:col-span-5">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="rounded-[32px] bg-forest p-7 text-white sm:p-8">
                <p className="font-serif font-extrabold text-lg text-sun">Selected course</p>
                {selectedCourse ? (
                  <>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="rounded-full bg-cream/10 px-3 py-1 text-xs font-bold">{L?.name}</span>
                    </div>
                    <h3 className="mt-3 text-2xl font-bold">{selectedCourse.name}</h3>
                    <p className="mt-1 text-sm text-white/60">{selectedCourse.description}</p>
                    <dl className="mt-6 space-y-3 border-t border-cream/10 pt-6 text-sm">
                      <div className="flex items-center justify-between">
                        <dt className="flex items-center gap-2 text-white/60"><CalendarDays className="h-4 w-4" />기간</dt>
                        <dd className="font-semibold">{selectedCourse.duration}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="flex items-center gap-2 text-white/60"><Clock className="h-4 w-4" />일정</dt>
                        <dd className="font-semibold">{selectedCourse.schedule}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="flex items-center gap-2 text-white/60"><Wallet className="h-4 w-4" />수강료</dt>
                        <dd className="font-serif font-extrabold text-xl text-sun">{selectedCourse.price.toLocaleString()}원</dd>
                      </div>
                    </dl>
                  </>
                ) : (
                  <div className="mt-4 flex items-center gap-4 rounded-2xl border border-dashed border-cream/20 p-5">
                    <GraduationCap className="h-8 w-8 shrink-0 text-white/40" />
                    <p className="text-sm text-white/70">과정을 선택하면 기간·일정·수강료가 여기에 표시됩니다.</p>
                  </div>
                )}
              </div>

              <div className="rounded-[32px] bg-white p-7 sm:p-8">
                <h2 className="text-xl font-bold text-ink">등록 절차</h2>
                <ol className="relative mt-6 space-y-5">
                  <span className="absolute bottom-3 left-[15px] top-3 w-px bg-cream-200" aria-hidden />
                  {steps.map((s, i) => (
                    <li key={s.title} className="relative flex gap-4">
                      <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream font-serif font-extrabold text-sm text-forest ring-4 ring-white">
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-bold text-ink">{s.title}</p>
                        <p className="text-sm text-muted">{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

export default function EnrollmentPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-cream" />}>
      <EnrollmentForm />
    </Suspense>
  );
}
